import { Component, input, output, signal, computed, effect, untracked, forwardRef, ViewChild, ElementRef, DestroyRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, debounceTime } from 'rxjs';
import { FaDuotoneIconComponent } from '@fortawesome/angular-fontawesome';
import { ChartOfAccountModel } from '../../core/models/data/chart-of-account.model';
import { ChartOfAccountCategory } from '../../core/enums/data.enums';
import { ChartOfAccountListSortOptions } from '../../core/enums/sort-options.enums';
import { ChartOfAccountService } from '../../core/services/chart-of-account/chart-of-account.service';
import { ApiSettings, PaginationConstants } from '../../core/constants/constants';
import { InViewPortDirective } from '../../core/directives/in-view-port.directive';
import { FormatLabelPipe } from '../../core/pipes/format-label/format-label.pipe';

@Component({
	selector: 'ms-chart-of-account-typeahead',
	standalone: true,
	imports: [CommonModule, FormsModule, FaDuotoneIconComponent, InViewPortDirective, FormatLabelPipe],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => ChartOfAccountTypeaheadComponent),
			multi: true
		}
	],
	templateUrl: './chart-of-account-typeahead.component.html',
	styleUrl: './chart-of-account-typeahead.component.scss'
})
export class ChartOfAccountTypeaheadComponent implements ControlValueAccessor {
	@ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;

	private chartOfAccountService = inject(ChartOfAccountService);
	private destroyRef = inject(DestroyRef);

	constructor() {
		// Reload from scratch whenever the organization or the fixed category restriction changes.
		// Reads of other signals inside loadAccounts (selectedCategory, searchTerm) must not become
		// dependencies of this effect, so the actual load runs untracked.
		effect(() => {
			const orgId = this.organizationId();
			this.categories();

			this.selectedCategory.set(null);
			if (orgId) {
				untracked(() => this.loadAccounts(true));
			} else {
				this.results.set([]);
				this.hasMore.set(false);
			}
		});

		// Debounce server-side search so we re-query on a pause in typing rather than on every keystroke.
		// Gated on isUserSearching: if the user selects an account (or the field is cleared/rewritten)
		// while a debounce timer from an earlier keystroke is still pending, that timer must not fire an
		// unnecessary reload with a stale search term once it resolves.
		this.searchInput$
			.pipe(
				debounceTime(PaginationConstants.SEARCH_DEBOUNCE_TIME_MILLISECONDS()),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe(() => {
				if (this.isUserSearching()) {
					this.loadAccounts(true);
				}
			});
	}

	// Only resolveSelectedAccount (an id written in from an existing record) can reach this with a
	// retired account — live search results are always filtered to isActive:true, so a fresh
	// selectAccount() call can never produce one. Flagging it here means every consumer that
	// displays a previously-saved account assignment (charge schedules, expenses, budgets, GL
	// entries) surfaces retirement without each of them checking isActive individually.
	private formatAccountLabel(account: ChartOfAccountModel): string {
		const label = `${account.accountNumber} - ${account.name}`;
		const withDescription = account.description ? `${label} - ${account.description}` : label;
		return account.isActive ? withDescription : `${withDescription} (Retired)`;
	}

	//#region Inputs

	organizationId = input<number | undefined>();
	categories = input<ChartOfAccountCategory[]>([]);
	excludeIds = input<number[]>([]);
	/**
	 * Accounts shown greyed out and unselectable rather than removed.
	 *
	 * Use this over excludeIds when the account is unavailable for a reason the user could act on —
	 * seeing "1000 Operating Cash" sitting there disabled tells them it is taken, where removing it
	 * leaves them hunting for an account they know exists.
	 */
	disabledIds = input<number[]>([]);
	/** Short suffix on a disabledIds option saying why it cannot be picked, e.g. "already in use". */
	disabledOptionNote = input<string>('');
	placeholder = input<string>('Search by account name...');
	disabled = input<boolean>(false);
	invalid = input<boolean>(false);

	//#endregion Inputs

	//#region Outputs

	// Emits the full resolved account alongside every CVA change so consumers that need more than
	// the id (category badge, retired flag, totals) don't have to maintain their own lookup/cache.
	accountSelected = output<ChartOfAccountModel | undefined>();

	//#endregion Outputs

	//#region Internal State

	searchTerm = signal<string>('');
	showDropdown = signal<boolean>(false);
	selectedIndex = signal<number>(-1);
	selectedCategory = signal<ChartOfAccountCategory | null>(null);
	private currentFormValue = signal<number | null>(null);
	private isUserSearching = signal<boolean>(false);

	results = signal<ChartOfAccountModel[]>([]);
	private skip = signal<number>(0);
	hasMore = signal<boolean>(false);
	isSearching = signal<boolean>(false);
	isLoadingMore = signal<boolean>(false);
	private canLoadMore = true;
	private readonly pageSize = ApiSettings.DEFAULT_TAKE();
	private searchInput$ = new Subject<string>();

	//#endregion Internal State

	//#region Computed

	// Chips shown above the results: the fixed category restriction when it names more than one
	// category, all six when unrestricted, or none at all when the picker is already locked to a
	// single category (nothing to filter).
	chipCategories = computed<ChartOfAccountCategory[]>(() => {
		const restricted = this.categories();
		if (restricted.length > 1) return restricted;
		if (restricted.length === 1) return [];
		return Object.values(ChartOfAccountCategory);
	});

	filteredAccounts = computed(() => {
		const exclude = new Set(this.excludeIds());
		return this.results().filter(a => !exclude.has(a.chartOfAccountId));
	});

	private disabledIdSet = computed(() => new Set(this.disabledIds()));

	public isSelected(account: ChartOfAccountModel): boolean {
		const value = this.currentFormValue();
		return value !== null && account.chartOfAccountId === value;
	}

	public isDisabledOption(account: ChartOfAccountModel): boolean {
		return this.disabledIdSet().has(account.chartOfAccountId);
	}

	/** Both states render the row unclickable, so every selection path checks them together. */
	public isUnselectable(account: ChartOfAccountModel): boolean {
		return this.isSelected(account) || this.isDisabledOption(account);
	}

	//#endregion Computed

	//#region Data Loading

	private async loadAccounts(reset: boolean): Promise<void> {
		const organizationId = this.organizationId();
		if (!organizationId) {
			this.results.set([]);
			this.hasMore.set(false);
			return;
		}

		if (reset) {
			this.skip.set(0);
			this.isSearching.set(true);
		} else {
			this.isLoadingMore.set(true);
		}

		const restricted = this.categories();
		const category = this.selectedCategory();
		const categories = category ? [category] : (restricted.length > 0 ? restricted : undefined);
		const term = this.isUserSearching() ? this.searchTerm().trim() : '';

		const result = await this.chartOfAccountService.searchChartOfAccounts(
			{ organizationId, categories, isActive: true, searchTerm: term || undefined },
			ChartOfAccountListSortOptions.AccountNumberAsc,
			this.skip(),
			this.pageSize
		);

		if (result.success && result.data) {
			const page = result.data.data ?? [];
			this.results.set(reset ? page : [...this.results(), ...page]);
			this.hasMore.set(result.data.hasNext ?? false);
			this.skip.set(this.skip() + page.length);
		} else if (reset) {
			this.results.set([]);
			this.hasMore.set(false);
		}

		this.isSearching.set(false);
		this.isLoadingMore.set(false);
	}

	private async resolveSelectedAccount(chartOfAccountId: number | null): Promise<void> {
		if (chartOfAccountId === null) {
			this.accountSelected.emit(undefined);
			return;
		}

		const loaded = this.results().find(a => a.chartOfAccountId === chartOfAccountId);
		if (loaded) {
			this.searchTerm.set(this.formatAccountLabel(loaded));
			this.accountSelected.emit(loaded);
			return;
		}

		const result = await this.chartOfAccountService.getChartOfAccountById(chartOfAccountId);
		if (result.success && result.data && this.currentFormValue() === chartOfAccountId) {
			this.searchTerm.set(this.formatAccountLabel(result.data));
			this.accountSelected.emit(result.data);
		}
	}

	public onLoadMore(): void {
		if (!this.hasMore() || !this.canLoadMore) return;

		this.canLoadMore = false;
		this.loadAccounts(false);
		setTimeout(() => {
			this.canLoadMore = true;
		}, PaginationConstants.LOAD_MORE_DEBOUNCE_TIME_MILLISECONDS());
	}

	//#endregion Data Loading

	//#region CVA

	private onChange: (value: any) => void = () => {};
	private onTouched: () => void = () => {};

	writeValue(value: any): void {
		const numericValue = typeof value === 'number' ? value : null;
		this.currentFormValue.set(numericValue);
		this.isUserSearching.set(false);

		if (numericValue === null) {
			this.searchTerm.set('');
			this.accountSelected.emit(undefined);
		} else {
			this.resolveSelectedAccount(numericValue);
		}
	}

	registerOnChange(fn: any): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: any): void {
		this.onTouched = fn;
	}

	//#endregion CVA

	//#region Event Handlers

	onInputChange(): void {
		this.isUserSearching.set(true);
		this.showDropdown.set(true);
		this.selectedIndex.set(-1);
		this.currentFormValue.set(null);
		this.onChange(null);
		this.accountSelected.emit(undefined);
		this.searchInput$.next(this.searchTerm());
	}

	onFocus(): void {
		this.showDropdown.set(true);
	}

	onBlur(): void {
		setTimeout(() => {
			this.showDropdown.set(false);
			this.selectedIndex.set(-1);
			this.onTouched();
		}, 200);
	}

	selectAccount(account: ChartOfAccountModel): void {
		this.isUserSearching.set(false);
		this.currentFormValue.set(account.chartOfAccountId);
		this.searchTerm.set(this.formatAccountLabel(account));
		this.showDropdown.set(false);
		this.selectedIndex.set(-1);
		this.onChange(account.chartOfAccountId);
		this.accountSelected.emit(account);
	}

	selectCategory(category: ChartOfAccountCategory | null): void {
		this.selectedCategory.set(category);
		this.selectedIndex.set(-1);
		this.loadAccounts(true);
	}

	/**
	 * Clears the displayed selection programmatically, for a host reusing this control across several
	 * openings — a modal that stays in the DOM, so the component is never recreated.
	 *
	 * Unlike clearSearch() this takes no focus and opens no dropdown: the caller is resetting a form,
	 * not reacting to the user pressing the clear button. It emits undefined so a host listening only
	 * to accountSelected cannot end up believing a selection survives.
	 */
	public reset(): void {
		this.isUserSearching.set(false);
		this.currentFormValue.set(null);
		this.searchTerm.set('');
		this.showDropdown.set(false);
		this.selectedIndex.set(-1);
		this.onChange(null);
		this.accountSelected.emit(undefined);
	}

	clearSearch(): void {
		this.isUserSearching.set(false);
		this.currentFormValue.set(null);
		this.searchTerm.set('');
		this.showDropdown.set(true);
		this.onChange(null);
		this.accountSelected.emit(undefined);
		setTimeout(() => this.searchInput?.nativeElement?.focus());
	}

	onKeyDown(event: KeyboardEvent): void {
		if (!this.showDropdown() || this.filteredAccounts().length === 0) return;

		switch (event.key) {
			case 'ArrowDown':
				event.preventDefault();
				this.selectedIndex.update(i => Math.min(i + 1, this.filteredAccounts().length - 1));
				this.scrollToSelected();
				break;
			case 'ArrowUp':
				event.preventDefault();
				this.selectedIndex.update(i => Math.max(i - 1, 0));
				this.scrollToSelected();
				break;
			case 'Enter': {
				event.preventDefault();

				// Read the row rather than trusting the index. The list can shrink between the arrow
				// press and this one — excludeIds changing as a caller resolves an account to hide, or a
				// reload replacing the results — leaving selectedIndex past the end. Indexing that threw.
				// A -1 index (nothing highlighted) falls out of the same check.
				const highlighted = this.filteredAccounts()[this.selectedIndex()];

				// Arrow keys still land on a disabled option; Enter must not take it.
				if (highlighted && !this.isUnselectable(highlighted)) {
					this.selectAccount(highlighted);
				}
				break;
			}
			case 'Escape':
				this.showDropdown.set(false);
				this.selectedIndex.set(-1);
				break;
		}
	}

	//#endregion Event Handlers

	//#region Private Methods

	private scrollToSelected(): void {
		const dropdown = document.querySelector('.coa-typeahead-dropdown .coa-items-list');
		const activeItem = dropdown?.querySelector('.dropdown-item.active');
		activeItem?.scrollIntoView({ block: 'nearest' });
	}

	//#endregion Private Methods
}
